<template>
	<v-row>
		<v-col>
			<ui-box
					new-forms
			>
				<form-title title="Active Items">
					<template v-slot:button>
						<v-row class="flex-nowrap pa-3">
							<v-btn
									class="mt-1 ml-6 rounded-lg"
									color="secondary"
									depressed
									outlined
									small
									@click="dialog.showAddItems = true"
							>
								<v-icon>
									mdi-plus
								</v-icon>
								Add Item
							</v-btn>
						</v-row>
					</template>
				</form-title>

				<v-row no-gutters>
					<v-col>
						<bw-table
								:key="items.length"
								aria-label="data-table"
								:headers="itemHeaders"
								:hide-default-footer="!items.length"
								:hide-default-header="!items.length"
								:hide-search="true"
								hide-row-select
								:items="items"
								id="items-table"
						>
							<template v-slot:no-data>
								<div
										class="align-center d-flex justify-center w-100"
										style="height: 47px;"
								>
									No added items yet
								</div>
							</template>
							<template v-slot:item.media="{ item }">
								<v-img
										:aspect-ratio="2"
										cover
										class="rounded"
										:key="item?.media.length ? item.media[0].id : -1"
										lazy-src="https://placehold.co/600x300?text=Image+Loading"
										max-width="120"
										:src="item?.media.length ? item.media[0].url : '/images/placehold.co.png'"
										:width="$vuetify.breakpoint.smAndUp ? '10vw' : '20vw'"
								></v-img>
							</template>
							<template v-slot:item.name="{ item }">
								<div v-if="item.venue_name" class="mb-2 text-body-2 font-italic">
									{{ item.venue_name }}
								</div>
								<div class="text-subtitle-2">
									<a :href="editItemLink(item)">
										{{ item.name }}
									</a>
								</div>
								<div class="grey--text mb-2 text-body-2 text--darken-1">
									{{ formatPublishStartEndDates(item) }}
								</div>
							</template>
							<template v-slot:item.retail_price="{ item }">
								<template v-if="!item.attributes.length">
									<div v-if="item.offer_price && item.offer_price != item.retail_price">
										<div class="text-decoration-line-through red--text">
											{{ `${currencyPrefix}${item.retail_price}` }}
										</div>
										<div>
											{{ `${currencyPrefix}${item.offer_price}` }}
										</div>
									</div>
									<div v-else>
										{{ `${currencyPrefix}${item.retail_price}` }}
									</div>
								</template>
								<template v-else>
									<template v-for="attribute in item.attributes">
										<v-row
												v-if="attribute.offer_price && attribute.offer_price != attribute.pivot.retail_price">
											<v-col>
												{{ attribute.name }}
											</v-col>
											<v-col>
												<div class="text-decoration-line-through red--text">
													{{
														`${currencyPrefix}${attribute.pivot.retail_price}`
													}}
												</div>
												<div>
													{{ `${currencyPrefix}${attribute.offer_price}` }}
												</div>
											</v-col>
										</v-row>
										<v-row v-else>
											<v-col>
												{{ attribute.name }}
											</v-col>
											<v-col>
												{{ `${currencyPrefix}${attribute.pivot.retail_price}` }}
											</v-col>
										</v-row>
									</template>
								</template>
							</template>
							<template v-slot:item.show_on_explore="{ item }">
								<div v-if="item.show_on_explore">
									<v-icon
											color="secondary"
											size="24"
									>
										mdi-check
									</v-icon>
								</div>
							</template>
							<template
									v-slot:item.status="{
										item: {
											id,
											type,
											statusClass,
											statusText,
											statusInfoTooltip,
											showStatusInfoTooltip
										}
									}"
							>
								<div :class="statusClass" style="white-space: nowrap;">
									{{ statusText }}
									<span v-if="statusInfoTooltip">
										<v-tooltip
												bottom
												v-model="showStatusInfoTooltip"
										>
    									<template v-slot:activator="{ on }">
												<v-icon
														class="warning--text"
														v-on="on"
														@click="showStatusInfoTooltip = !showStatusInfoTooltip"
												>
									        mdi-information-outline
      									</v-icon>
    									</template>
											<div v-html="statusInfoTooltip" class="text-center"></div>
  									</v-tooltip>
									</span>
								</div>
							</template>
							<template v-slot:item.actions="{ item }">
								<div class="d-flex justify-end">
									<v-btn
											color="secondary"
											depressed
											fab
											outlined
											small
											@click="editItemDialog(item)"
									>
										<v-icon>
											mdi-pencil-outline
										</v-icon>
									</v-btn>
									<v-divider
											class="mx-2 my-1"
											vertical
									></v-divider>
									<v-btn
											color="secondary"
											depressed
											fab
											outlined
											small
											@click="settingsItemDialog(item)"
									>
										<v-icon>
											mdi-cog-outline
										</v-icon>
									</v-btn>
									<v-divider
											class="mx-2 my-1"
											vertical
									></v-divider>
									<v-btn
											color="error"
											depressed
											fab
											outlined
											small
											@click="removeItemDialog(item)"
									>
										<v-icon>
											mdi-trash-can-outline
										</v-icon>
									</v-btn>
								</div>
							</template>
						</bw-table>
					</v-col>
				</v-row>
			</ui-box>
			<!-- Add Items Dialog -->
			<v-dialog
					v-model="dialog.showAddItems"
					content-class="rounded-xl"
					persistent
					scrollable
					width="1200px"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Available Items</h6>
							</div>
						</div>
					</v-card-title>
					<!-- Available Items List -->
					<v-card-text class="text-body-2">
						<v-row>
							<v-col>
								<bw-table
										:key="availableItems.length"
										aria-label="data-table"
										:display-filters="availableItemDisplayFilters"
										:headers="availableItemHeaders"
										:items="availableItems"
										hide-row-select
										:hide-reset="true"
										:sortBy="['created_at']"
										:sortDesc="true"
										style="{line-height: 24px;}"
								>
									<template v-slot:item.selected="{ item }">
										<v-checkbox
												v-model="item.selected"
												@change="updateSelectedItems"
										></v-checkbox>
									</template>
									<template v-slot:item.type="{ value }">
										{{ formatType(value) }}
									</template>
									<template v-slot:item.created_at="{ value }">
										{{ formatDate(value) }}
									</template>
								</bw-table>
							</v-col>
						</v-row>
					</v-card-text>
					<!-- Add Items Buttons -->
					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="dialog.showAddItems = false"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="selectedItems.length === 0"
										:large="$vuetify.breakpoint.mdAndUp"
										@click="addSelectedItems"
								>
									Add
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<!-- Advanced Settings Dialog -->
			<bw-modal v-if="settingsItem" title="Advanced Settings" :width="800">
				<template #body>
					<v-row>
						<v-col cols="12" md="6">
							<v-card
									class="preview-card mx-auto"
									elevation="6"
									max-width="350"
									tile
							>
								<div class="position-relative">
									<div
											v-if="settingsItem.tag_text"
											class="font-weight-medium pa-2 position-absolute text-caption text-uppercase z-2"
											:style="{
												backgroundColor: settingsItem.tag_color,
												color: textColor,
												lineHeight: '1rem'
											}"
									>
										{{ settingsItem.tag_text }}
									</div>
									<v-img
											:aspect-ratio="2"
											class="z-1"
											cover
											lazy-src="https://placehold.co/600x300?text=Image+Loading"
											:src="settingsItem?.media?.[0]?.url || '/images/placehold.co.png'"
									></v-img>
								</div>

								<v-card-text class="px-8 pb-0">

									<div class="font-weight-bold mb-2 text-body-1">
										{{ settingsItem.name }}
									</div>

									<ul v-if="getItemHighlights(settingsItem).length" class="ps-4">
										<li
												v-for="(highlight, idx) in getItemHighlights(settingsItem)"
												:key="`highlight-${settingsItem.id}-${idx}`"
												class="text-caption"
										>
											{{ highlight }}
										</li>
									</ul>

								</v-card-text>

								<v-card-actions class="d-block pa-8">

									<v-row v-if="settingsItem.show_price">
										<v-col cols="12">
											<div
													:class="[
															'font-weight-medium',
															'grey lighten-4',
															'px-2 py-0',
															'rounded-sm',
															'text-body-2'
													]"
													style="height: 1.5rem; line-height: 1.5rem;"
													v-html="getSettingsItemPrice(settingsItem)"
											></div>
										</v-col>
									</v-row>

									<v-row>
										<v-col
												class="pt-0"
												:class="{
													'pe-1': settingsItem.detail_page
												}"
										>
											<v-btn
													class="font-weight-bold px-0 rounded text-capitalize w-100"
													:color="getBtnColor(settingsItem)"
													depressed
													style="height: 36px !important;"
													:style="{ color: getBtnTextColor(settingsItem) }"
											>
												{{ settingsItem.cta_text || 'Add To Cart' }}
											</v-btn>
										</v-col>
										<v-col
												v-if="settingsItem.detail_page"
												class="ps-1 pt-0"
										>
											<v-btn
													class="font-weight-bold px-0 rounded text-capitalize w-100"
													color="grey darken-2"
													depressed
													outlined
													style="height: 36px !important;"
											>
												Learn More
											</v-btn>
										</v-col>
									</v-row>
								</v-card-actions>
							</v-card>
						</v-col>
						<v-col cols="12" md="6">
							<v-form
									ref="advancedSettingsForm"
									id="advanced-settings"
							>

								<form-subsection title="Show Item Card Sections"/>

								<v-row class="mt-0">
									<v-col cols="6">
										<v-switch
												v-model="settingsItem.show_price"
												class="mt-0"
												dense
												:false-value="0"
												hide-details="auto"
												inset
												label="Pricing"
												:true-value="1"
												@change="onAdvancedSettingsFormInput"
										></v-switch>
									</v-col>
									<v-col cols="6">
										<v-switch
												v-model="settingsItem.detail_page"
												class="mt-0"
												dense
												:false-value="0"
												hide-details="auto"
												inset
												label="Learn More"
												:true-value="1"
												@change="onAdvancedSettingsFormInput"
										></v-switch>
									</v-col>
								</v-row>

								<v-row>
									<v-col>
										<v-text-field
												v-if="showMaxPurchaseQuantity"
												v-model.number="settingsItem.max_quantity"
												class="rounded-lg"
												hide-details="auto"
												label="Max Purchase Quantity"
												outlined
												type="number"
												@input="onAdvancedSettingsFormInput"
												@wheel.native.prevent
										></v-text-field>
									</v-col>
								</v-row>

								<v-row>
									<v-col>
										<v-text-field
												v-model="settingsItem.cta_text"
												class="rounded-lg"
												hide-details="auto"
												label="CTA Button Label"
												outlined
												@input="onAdvancedSettingsFormInput"
										></v-text-field>
									</v-col>
								</v-row>

								<v-row>
									<v-col>
										<v-text-field
												v-model="settingsItem.tag_text"
												class="rounded-lg"
												hide-details="auto"
												label="Tag Text"
												outlined
												@input="onAdvancedSettingsFormInput"
										></v-text-field>
									</v-col>
								</v-row>

								<v-row>
									<v-col>
										<v-row>
											<v-col>
												<v-row no-gutters>
													<v-col>
														<v-text-field
																v-model="tempTagColor"
																class="rounded-l-lg rounded-r-0"
																hide-details="auto"
																label="Tag Color"
																outlined
																:rules="rules.hexColor"
																@change="updateTagColor"
																@blur="updateTagColor"
														></v-text-field>
													</v-col>
													<v-col cols="auto">

														<v-dialog
																v-model="tagColorMenu"
																content-class="rounded-xl"
																persistent
																scrollable
																width="400px"
														>
															<template v-slot:activator="{ on }">
																<v-btn
																		v-on="on"
																		class="px-2 rounded-l-0 rounded-r-lg"
																		color="secondary"
																		depressed
																		outlined
																		style="border-left: none; min-width: 56px;"
																		x-large
																		@click="onAdvancedSettingsFormInput"
																>
																	<v-icon class="mx-0">mdi-palette</v-icon>
																</v-btn>
															</template>
															<v-card class="position-relative" rounded="xl">
																<v-card-title
																		class="d-flex justify-space-between ma-0 ma-md-4">
																	<div class="w-100">
																		<div
																				class="align-center d-flex justify-space-between">
																			<h6>Tag Color</h6>
																			<v-btn
																					class="mt-n1 me-n2"
																					color="black"
																					depressed
																					icon
																					mode="hexa"
																					@click="cancelTagColor"
																			>
																				<v-icon>mdi-close</v-icon>
																			</v-btn>
																		</div>
																	</div>
																</v-card-title>
																<v-card-text class="text-body-2">
																	<v-color-picker
																			v-model="tagColorPicked"
																			ref="colorPicker"
																			class="rounded-lg"
																			dot-size="20"
																			@input="(e) => tempTagColor = e"
																	/>
																</v-card-text>
																<v-card-actions>
																	<div class="w-100">
																		<div
																				class="align-center d-flex justify-space-between">
																			<span></span>
																			<div class="d-flex justify-end">
																				<v-btn
																						class="me-2 me-md-4 rounded-lg"
																						color="red darken-1"
																						depressed
																						:large="$vuetify.breakpoint.mdAndUp"
																						outlined
																						@click="resetTagColor"
																				>
																					Reset
																				</v-btn>
																				<v-btn
																						class="me-2 me-md-4 rounded-lg"
																						color="grey darken-1"
																						depressed
																						:large="$vuetify.breakpoint.mdAndUp"
																						text
																						@click="cancelTagColor"
																				>
																					Cancel
																				</v-btn>
																				<v-btn
																						class="rounded-lg"
																						color="primary"
																						depressed
																						:large="$vuetify.breakpoint.mdAndUp"
																						@click="handleAcceptTagColor"
																				>
																					Accept
																				</v-btn>
																			</div>
																		</div>
																	</div>
																</v-card-actions>
															</v-card>
														</v-dialog>
													</v-col>
												</v-row>
											</v-col>
											<v-col class="d-flex" cols="auto">
												<v-btn
														class="px-1 px-sm-2 rounded-lg"
														:color="tempTagColor"
														depressed
														:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
														:style="{ color: textColor }"
														x-large
												>
													<template v-if="$vuetify.breakpoint.xsOnly">T</template>
													<template v-else>TAG</template>
												</v-btn>
											</v-col>
										</v-row>

										<template v-if="tagTextWarning">
											<v-alert
													class="mb-0 mt-8 rounded-lg"
													border="left"
													color="warning"
													dense
													icon="mdi-alert-circle"
													text
											>
												<span class="text-body-2">
													<span class="font-weight-bold">Warning: </span>
													For better contrast you may want to pick a darker color.
												</span>
											</v-alert>
										</template>

									</v-col>
								</v-row>
							</v-form>
						</v-col>
					</v-row>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<span></span>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="cancelAdvancedSettings"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="!settingsItem.dirty || isSaving"
									:large="$vuetify.breakpoint.mdAndUp"
									@click="updateAdvancedSettings"
							>
								Update
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>

			<!-- Edit Item Dialog -->
			<v-dialog
					v-if="editItem"
					content-class="rounded-xl"
					persistent
					scrollable
					width="900px"
					:value="true"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Edit Item</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">
						<v-form
								ref="editItemForm"
								id="edit-item"
						>
							<ui-box
									new-forms
							>

								<form-subsection title="Include on Explore"/>

								<v-row v-if="!editItem.paid && editItem?.type === 'deal'">
									<v-col>
										<v-switch
												v-model="editItem.show_on_explore"
												class="mt-0"
												dense
												hide-details="auto"
												inset
												label="Allow anyone to sign up for this pass on the Mobile Pass Explore tab"
												@change="onEditItemFormInput"
										></v-switch>
									</v-col>
								</v-row>
								<v-row v-else>
									<v-col>
										<span>As this item is not free it can not be added to the Explore tab.</span>
									</v-col>
								</v-row>

								<form-divider/>

								<form-subsection title="Price & Discounts"/>

								<v-row v-if="editItem.paid || editItem?.type === 'product'">
									<v-col>
										<template v-if="!editItem.attributes.length">
											<v-row>
												<v-col cols="6">
													<v-text-field
															v-model.number="editItem.retail_price"
															class="mt-4 mt-sm-0 rounded-lg"
															disabled
															hide-details="auto"
															label="Retail Price"
															outlined
															:prefix="currencyPrefix"
															readonly
															type="number"
															@wheel.native.prevent
													></v-text-field>
												</v-col>
												<v-col cols="6"
															 v-if="editItem.retail_price != editItem.offer_price">
													<v-text-field
															v-model.number="editItem.offer_price"
															class="mt-4 mt-sm-0 rounded-lg"
															disabled
															hide-details="auto"
															label="Discounted Price"
															outlined
															:prefix="currencyPrefix"
															readonly
															type="number"
															@wheel.native.prevent
													></v-text-field>
												</v-col>
											</v-row>
											<v-row v-if="editItem.discounts.length">
												<v-col cols="12" sm="9" md="6">
													<v-row>
														<v-col cols="4" md="3">
															<v-select
																	v-model="editItem.discounts[0].type"
																	class="rounded-lg"
																	hide-details="auto"
																	:items="[{name: currencyPrefix, value: 'fixed'}, {name: '%', value: 'percent'}]"
																	item-text="name"
																	label="Discount Type"
																	outlined
																	width="80"
																	@change="onEditItemFormInput"
															></v-select>
														</v-col>
														<v-col cols="8" md="9">
															<v-text-field
																	v-model.number="editItem.discounts[0].amount"
																	class="rounded-lg"
																	hide-details="auto"
																	label="Discount Amount"
																	outlined
																	:prefix="editItem.discounts[0].type === 'fixed' ? `${currencyPrefix} ` : null"
																	:suffix="editItem.discounts[0].type === 'percent' ? ' %' : null"
																	type="number"
																	@input="onEditItemFormInput"
																	@wheel.native.prevent
															></v-text-field>
														</v-col>
													</v-row>
												</v-col>
											</v-row>

											<v-row v-else>
												<v-col>
													No discounts.
												</v-col>
											</v-row>
										</template>
										<template v-else>
											<template v-for="(attribute, index) in editItem.attributes">
												<v-row>
													<v-col>
														{{ attribute.name }}
													</v-col>
												</v-row>
												<v-row>
													<v-col cols="6">
														<v-text-field
																v-model.number="attribute.pivot.retail_price"
																class="mt-4 mt-sm-0 rounded-lg"
																disabled
																hide-details="auto"
																label="Retail Price"
																outlined
																:prefix="currencyPrefix"
																readonly
																type="number"
																@wheel.native.prevent
														></v-text-field>
													</v-col>
													<v-col cols="6"
																 v-if="attribute.pivot.retail_price != attribute.offer_price">
														<v-text-field
																v-model.number="attribute.offer_price"
																class="mt-4 mt-sm-0 rounded-lg"
																disabled
																hide-details="auto"
																label="Discounted Price"
																outlined
																:prefix="currencyPrefix"
																readonly
																type="number"
																@wheel.native.prevent
														></v-text-field>
													</v-col>
												</v-row>
												<v-row>
													<v-col cols="12" sm="9" md="6">
														<v-row>
															<v-col cols="4" md="3">
																<v-select
																		v-model="getDiscount(attribute.id).type"
																		class="rounded-lg"
																		hide-details="auto"
																		:items="[{name: currencyPrefix, value: 'fixed'}, {name: '%', value: 'percent'}]"
																		item-text="name"
																		label="Discount Type"
																		outlined
																		width="80"
																		@change="onEditItemFormInput"
																></v-select>
															</v-col>
															<v-col cols="8" md="9">
																<v-text-field
																		v-model.number="getDiscount(attribute.id).amount"
																		class="rounded-lg"
																		hide-details="auto"
																		label="Discount Amount"
																		outlined
																		:prefix="getDiscount(attribute.id).type === 'fixed' ? `${currencyPrefix} ` : null"
																		:suffix="getDiscount(attribute.id).type === 'percent' ? ' %' : null"
																		type="number"
																		@input="onEditItemFormInput"
																		@wheel.native.prevent
																></v-text-field>
															</v-col>
														</v-row>
													</v-col>
												</v-row>

												<form-divider inner
																			v-if="index < editItem.attributes.length - 1"/>

											</template>
										</template>
									</v-col>
								</v-row>
								<v-row v-else>
									<v-col>
										<span>As this {{itemTypeLabel(editItem.type)}} is free, there are no prices or discounts to edit.</span>
									</v-col>
								</v-row>
							</ui-box>
						</v-form>
					</v-card-text>
					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="editItem = null"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="!editItem.dirty || isSaving"
										:large="$vuetify.breakpoint.mdAndUp"
										@click="updateEditItem"
								>
									Update
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<confirm-dialog
					:open="itemToRemove !== null"
					:description="confirmText"
					title="Remove Item"
					@cancel="() => itemToRemove = null"
					@confirm="removeItem"
			></confirm-dialog>

		</v-col>
	</v-row>
</template>

<script>
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';
import {copyToClipboard, helpers} from '../../../../Shared/helpers';
import FormSubsection from "../../../../Shared/UI/Form/FormSubsection.vue";
import kebabCase from 'lodash/kebabCase';

export default {
	name: 'ChannelItems',
	components: {
		FormSubsection,
		ConfirmDialog,
	},
	props: {
		availableItems: {
			type: Array,
			default: () => []
		},
		channel: {
			type: Object,
			default: () => ({})
		},
		items: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		},
		extraData: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			availableItemDisplayFilters: {
				types: {
					name: 'Type',
					type: 'select',
					data: [
						{
							id: 'package',
							name: 'Packages',

						},
						{
							id: 'deal',
							name: 'Passes',

						},
						{
							id: 'product',
							name: 'Products',

						}
					],
					position: 'main',
					clearable: true,
					filterOn: 'type',
				},
			},
			availableItemHeadersData: [
				{
					align: 'start',
					mandatory: true,
					text: 'Select',
					value: 'selected',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Item ID',
					value: 'id',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Merchant',
					value: 'venue',
					width: '20%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Type',
					value: 'type',
					width: '10%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Date Created',
					value: 'created_at',
					width: '1%'
				}
			],
			confirmText: `<strong>Please click "Confirm" to remove this item from this
				Channel.</strong><br />This will cause the item to be removed from any
				website you have embedded it on, including the Explore Tab of your
				mobile pass if you have that feature enabled. You can always add the
				item back at any time.`,
			currencyPrefix: this.partner.currencyPrefix,
			isSaving: false,
			itemHeadersData: [
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Image',
					value: 'media',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Price',
					value: 'retail_price',
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Explore',
					value: 'show_on_explore',
					width: '5%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Status',
					value: 'status',
					width: '5%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Actions',
					sortable: false,
					value: 'actions',
					width: '1%'
				}
			],
			itemToRemove: null,
			selectedItems: [],
			settingsItem: null,
			editItem: null,
			dialog: {
				showAddItems: false
			},
			rules: {
				hexColor: [v => !v || /^#[0-9A-F]{6}$/i.test(v) || 'Invalid HEX color (e.g. #2a73c5)']
			},
			tagColorMenu: false,
			tagColorPicked: null,
			tagColor: null,
			tagTextWarning: false,
			tempTagColor: null,
			textColor: null
		}
	},
	created() {
		this.availableItems.forEach((item) => {
			Object.assign(item, { selected: false });
		});
		this.items.forEach(this.formatItem);
	},
	mounted() {
		// default color
		const setColor = '#2a73c5';
		this.tagColor = setColor;
		this.tagColorPicked = setColor;
		this.tempTagColor = setColor;

		// Until we're saving the text color default to white
		this.textColor = '#ffffff';
	},
	computed: {
		availableItemHeaders() {
			return this.availableItems?.length ? this.availableItemHeadersData : [];
		},
		itemHeaders() {
			return this.items?.length ? this.itemHeadersData : [];
		},
		hasShowOnExplore() {
			return this.items?.length && this.items.some(item => item.show_on_explore);
		},
		timezone() {
			return this.partner.timezone;
		},
		showMaxPurchaseQuantity() {
			return this.settingsItem.template_settings?.max_offer_quantity != 1
		}
	},
	methods: {
		kebabCase,
		copyToClipboard,
		cancelAdvancedSettings() {
			this.tagTextWarning = false;
			this.settingsItem = null;
		},

		editItemLink({ id, type }) {
			const subpath = type === 'product' ? 'products/edit-product' : 'passes';
			return `/partner-portal/${this.partner.id}/${subpath}/${id}/name`;
		},

		formatItem(item) {
			let sumOfferPrice = item.offer_price || 0;
			item.offer_price = this.formatPrice(item.offer_price);
			item.retail_price = this.formatPrice(item.retail_price);
			const hasOfferDiscounts = !!item.discounts.length;
			const hasNullOfferDiscount = !hasOfferDiscounts || (hasOfferDiscounts && ((item.discounts[0].type === null && item.discounts[0].amount === 0) || (item.discounts[0].type === 'fixed' && item.discounts[0].amount === 0) || (item.discounts[0].type === 'percent' && item.discounts[0].amount === 0)));
			if(!hasOfferDiscounts) {
				item.discounts = [{ type: null, amount: 0 }];
			}
			if(item.attributes.length) {
				item.attributes.forEach((attribute) => {
					sumOfferPrice += attribute.offer_price || 0;
					attribute.offer_price = this.formatPrice(attribute.offer_price);
					attribute.pivot.retail_price = this.formatPrice(attribute.pivot.retail_price);
					const containsAttribute = item.discounts.some((offerDiscount) => offerDiscount.attribute_id === attribute.id);
					if(!hasOfferDiscounts || !containsAttribute) {
						item.discounts.push({ attribute_id: attribute.id, type: null, amount: 0 });
					}
				});
			}
			// item.isFree = sumOfferPrice === 0 && hasNullOfferDiscount;
			const [statusText, statusClass, statusInfoTooltip] = this.calculateStatus(item);
			item.statusText = statusText;
			item.statusClass = statusClass;
			item.statusInfoTooltip = statusInfoTooltip;
			item.showStatusInfoTooltip = false;
			item.show_on_explore = !!item.show_on_explore;
		},

		getBtnColor(item) {
			const partnerColor = this.partner?.styles?.colors?.primary?.main;
			const channelColor = this.channel?.styles?.colors?.primary?.main;
			return channelColor || partnerColor || '#2a73c5';
		},

		getBtnTextColor(item) {
			// Only use the channel color if we have both
			const hasChannelMainColor = !!this.channel?.styles?.colors?.primary?.main;

			const partnerColor = this.partner?.styles?.colors?.primary?.backgroundText;
			const channelColor = this.channel?.styles?.colors?.primary?.backgroundText;
			return hasChannelMainColor && channelColor || partnerColor || '#ffffff';
		},

		getDiscount(attributeId) {
			let discount = this.editItem.discounts.find(disccount => disccount.attribute_id === attributeId);
			if(!discount) {
				discount = { attribute_id: attributeId, type: null, amount: 0 };
				this.editItem.discounts.push(discount);
			}
			return discount;
		},

		getItemHighlights(settingsItem) {
			return settingsItem.highlights ? settingsItem.highlights.split('\n') : [];
		},

		itemTypeLabel(type) {
			const lables = {
				deal: 'pass',
				package: 'package',
				product: 'product'
			};
			return lables[type] || 'item';
		},

		addSelectedItems() {
			const url = `/partner-portal/${this.partner.id}/channels/add-items/${this.channel.id}`;
			this.dialog.showAddItems = false;
			this.isSaving = true;
			axios.post(url, { items: this.selectedItems })
					.then(({ data: { availableItems, items } }) => {
						if(items) {
							items.forEach(this.formatItem);
						}
						this.availableItems.splice(0, this.availableItems.length, ...availableItems);
						this.items.splice(0, this.items.length, ...items);
						this.$emit('update-data', 'items', this.items);
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.isSaving = false;
					});
		},
		calculateStatus({
			active,
			active_dates: activeDates = [],
			parent_available: parentAvailable,
			publish_start_date: startDate,
			publish_end_date: endDate,
			type
		}) {
			if(!active || parentAvailable === 'PARENT_ITEM_INACTIVE' || parentAvailable === 'PARENT_ITEM_INACTIVE_BY_DATES') {
				const typeNameCap = type === 'deal' ? 'Pass' : 'Product';
				const typeName = typeNameCap.toLocaleLowerCase();

				let inactiveMessage;
				switch (parentAvailable) {
					case 'PARENT_ITEM_INACTIVE':
						inactiveMessage = `This is inactive because <br />the ${typeName} is set to inactive.`;

						break;
					case 'PARENT_ITEM_INACTIVE_BY_DATES':
						inactiveMessage = `<div>This is inactive because the ${typeName}<br /> is set to inactive by its dates.</div>`;

						if (activeDates?.length) {
							inactiveMessage += `<div class="caption mt-2">${typeNameCap} Availability</div><div class="caption">`;
							activeDates.map(({ end_date: e, start_date: s }) => {
								const end = e ? this.formatDateLong(e) : null;
								const start = s ? this.formatDateLong(s) : null;
								if (start && end) {
									inactiveMessage += `${start} - ${end}`;
								} else if (start) {
									inactiveMessage += `From ${start}`;
								} else if (end) {
									inactiveMessage += `Until ${end}`;
								}
								inactiveMessage += '<br />';
							});
							inactiveMessage += '</div>';
						}
						break;
				}
				return ['Inactive', 'warning--text', inactiveMessage];
			}

			const now = moment();
			if(endDate && moment(endDate).isBefore(now)) {
				return ['Inactive', 'warning--text', null];
			}
			if(!startDate || moment(startDate).isBefore(now)) {
				return ['Active', '', null];
			}
			return ['Inactive', 'warning--text', null];
		},
		formatDate(date) {
			return moment.utc(date).tz(this.timezone).format('L');
		},
		formatDateLong(date) {
			return moment.utc(date).tz(this.timezone).format('MMMM Do, YYYY');
		},
		formatPublishStartEndDates({
			active,
			active_dates: activeDates,
			parent_available: parentAvailable
		}) {
			if (!active || parentAvailable === 'PARENT_ITEM_INACTIVE')
				return '';

			if (!activeDates?.length)
				return 'Active Indefinitely';

			// find current
			const current = activeDates.find(({ end_date: e, start_date: s }) => {
				const end = e ? moment(e) : null;
				const start = s ? moment(s) : null;
				if (
					(start && end && moment().isBetween(start, end)) ||
					(start && !end && moment().isAfter(start)) ||
					(!start && end && moment().isBefore(end))
				) {
					return true;
				}
				return false;
			});

			if (current) {
				const { end_date: end, start_date: start } = current;
				if (!end) {
					return `Active from ${this.formatDateLong(start)}`;
				}
				if (!start) {
					return `Active until ${this.formatDateLong(end)}`;
				}
				return `${this.formatDateLong(start)} - ${this.formatDateLong(end)}`;
			}

			// no current? find future
			const future = activeDates.find(({ start_date: s }) => {
				const start = s ? moment(s) : null;
				if (start && moment().isBefore(start)) {
					return true;
				}
				return false;
			});
			if (future)
				return `Active from ${this.formatDateLong(future.start_date)}`;

			// no future? find past
			const past = activeDates
				.slice()
				.reverse()
				.find(({ end_date: e}) => {
				const end = e ? moment(e) : null;
				if (end && moment().isBefore(end)) {
					return true;
				}
				return false;
			});
			if (past)
				return `Active until ${this.formatDateLong(past.end_date)}`;
		},
		formatType(type) {
			const types = {
				deal: 'Pass',
				product: 'Product',
			};
			return types[type] || '';
		},
		formatPrice(value) {
			const numericValue = parseFloat(value);
			return isNaN(numericValue) ? '0.00' : numericValue.toFixed(2).toString();
		},
		getItemById(itemId) {
			return this.items.find(({ id }) => id === itemId);
		},

		getSettingsItemPrice(settingsItem) {
			const { currencyPrefix } = this;

			if(settingsItem.attributes.length === 0) {
				if(settingsItem.offer_price && settingsItem.offer_price !== settingsItem.retail_price) {
					return `<span class="grey--text text--darken-1 text-decoration-line-through">${currencyPrefix}${parseFloat(settingsItem.retail_price).toFixed(2)}</span> - ${currencyPrefix}${parseFloat(settingsItem.offer_price).toFixed(2)}`;
				}
				else {
					return settingsItem.retail_price === '0.00'
							? 'FREE'
							: `${currencyPrefix}${parseFloat(settingsItem.retail_price).toFixed(2)}`;
				}
			}
			else {
				// Retail
				let lowRetailPrice = parseFloat(settingsItem.attributes[0].pivot.retail_price);
				let highRetailPrice = parseFloat(settingsItem.attributes[0].pivot.retail_price);

				// Offer
				let lowOfferPrice = parseFloat(settingsItem.attributes[0].offer_price);
				let highOfferPrice = parseFloat(settingsItem.attributes[0].offer_price);

				for (let i = 1; i < settingsItem.attributes.length; i++) {
					let retailPrice = parseFloat(settingsItem.attributes[i].pivot.retail_price);
					let offerPrice = parseFloat(settingsItem.attributes[i].offer_price);

					// Retail
					lowRetailPrice = retailPrice < lowRetailPrice ? retailPrice : lowRetailPrice;
					highRetailPrice = retailPrice > highRetailPrice ? retailPrice : highRetailPrice;

					// Offer
					lowOfferPrice = offerPrice < lowOfferPrice ? offerPrice : lowOfferPrice;
					highOfferPrice = offerPrice > highOfferPrice ? offerPrice : highOfferPrice;
				}

				// Retail text
				let lowRetailPriceText = `${currencyPrefix}${lowRetailPrice.toFixed(2)}`;
				let highRetailPriceText = `${currencyPrefix}${highRetailPrice.toFixed(2)}`;

				// Offer text
				let lowOfferPriceText = `${currencyPrefix}${lowOfferPrice.toFixed(2)}`;
				let highOfferPriceText = `${currencyPrefix}${highOfferPrice.toFixed(2)}`;

				// If retail is more than offer
				if(lowRetailPrice !== lowOfferPrice) {
					return `<span class="grey--text text--darken-1 text-decoration-line-through">${lowRetailPriceText} - ${highRetailPriceText}</span> ${lowOfferPriceText} - ${highOfferPriceText}`;
				}

				if(lowOfferPrice !== highOfferPrice) {
					return `${lowOfferPriceText} - ${highOfferPriceText}`;
				}
				else {
					return lowOfferPriceText;
				}
			}
		},
		onAdvancedSettingsFormInput(e) {
			this.settingsItem.dirty = true;
		},
		onEditItemFormInput(e) {
			this.editItem.dirty = true;
		},
		editItemDialog(item) {
			this.editItem = { ...item };
		},
		settingsItemDialog(item) {
			this.settingsItem = { ...item };
			const setColor = this.settingsItem?.tag_color || '#2a73c5';
			this.tagColor = setColor;
			this.tagColorPicked = setColor;
			this.tempTagColor = setColor;
			this.textColor = helpers.getContrastTextColor(setColor);
			this.tagTextWarning = helpers.getContrastTextColor(setColor) === "#000000";
		},
		removeItemDialog(item) {
			this.itemToRemove = { id: item.id, type: item.type };
		},
		removeItem() {
			const url = `/partner-portal/${this.partner.id}/channels/remove-item/${this.channel.id}`;
			this.isSaving = true;
			axios.post(url, { item: this.itemToRemove })
					.then(({ data: { availableItems, items } }) => {
						this.availableItems.splice(0, this.availableItems.length, ...availableItems);
						if(items) {
							items.forEach(this.formatItem);
						}
						this.items.splice(0, this.items.length, ...items);
						this.$emit('update-data', 'items', this.items);
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.itemToRemove = null;
						this.isSaving = false;
					});
		},
		updateEditItem() {
			const {
				id,
				type,
				publish_start_date,
				publish_end_date,
				show_on_explore,
				discounts,
				retail_price
			} = this.editItem;

			const url = `/partner-portal/${this.partner.id}/channels/update-item/${this.channel.id}`;

			const itemData = {
				id,
				type,
				publish_start_date,
				publish_end_date,
				show_on_explore,
				discounts,
				retail_price
			};

			this.isSaving = true;

			axios.post(url, { item: itemData })
					.then(({ data }) => {

						this.items.forEach((item, index) => {
							if(item.id === id) {
								this.formatItem(data.item);
								this.$set(this.items, index, { ...item, ...data.item });
							}
						});
						this.editItem = null;
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.isSaving = false;
						this.updateSecondaryMenu();
						this.$emit('update-data', 'items', this.items);
					})
			;
		},
		
		updateAdvancedSettings() {
			if(!this.settingsItem.max_quantity) {
				this.settingsItem.max_quantity = 0;
			}
			const {
				id,
				type,
				show_price,
				detail_page,
				max_quantity,
				cta_text,
				tag_text,
				tag_color
			} = this.settingsItem;

			const url = `/partner-portal/${this.partner.id}/channels/update-item/${this.channel.id}`;

			const itemData = {
				id,
				type,
				show_price,
				detail_page,
				max_quantity,
				cta_text,
				tag_text,
				tag_color
			};

			this.isSaving = true;

			axios.post(url, { item: itemData })
					.then(({ data }) => {
						this.items.forEach((item, index) => {
							if(item.id === id) {
								this.formatItem(data.item);
								this.$set(this.items, index, { ...item, ...data.item });
							}
						});
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.isSaving = false;
						this.cancelAdvancedSettings();
					})
			;
		},

		updateSecondaryMenu() {
			window.EventBus.$emit('get-updated-menu');
		},

		updateSelectedItems() {
			const items = [];
			this.availableItems.forEach(({ selected, id, type }) => {
				if(selected) {
					items.push({ id: id, type: type });
				}
			});
			this.selectedItems.splice(0, this.selectedItems.length, ...items);
		},
		resetTagColor() {
			this.tagColorMenu = false;
			this.textColor = null;
			this.tempTagColor = null;
			this.settingsItem.tag_color = null;
		},
		cancelTagColor() {
			this.tagColorMenu = false;
			this.tempTagColor = this.settingsItem?.tag_color;
		},
		updateTagColor() {
			this.settingsItem.tag_color = this.tempTagColor;
			this.tagColorPicked = this.tempTagColor;
			this.settingsItem.tag_color = this.tempTagColor;
			// TODO: For when we add tag_text_color to the offer_deals table
			//this.settingsItem.tag_text_color. = helpers.getContrastTextColor(this.primaryColor);
			this.tagTextWarning = helpers.getContrastTextColor(this.tempTagColor) === "#000000";
		},
		
		updateTextColor(color) {
			this.textColor = color !== null ? helpers.getContrastTextColor(color) : null;
		},
		handleAcceptTagColor() {
			this.tagColorMenu = false;
			this.updateTagColor();
		},
	},
};
</script>

<style lang="scss" scoped>
/*::v-deep #items-table td {
	vertical-align: top !important;
}*/
.preview-card * {
	letter-spacing: -0.05px !important;
}
</style>
